import { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Tag,
  Table,
  Modal,
  Form,
  App,
  Popconfirm,
  Select,
  Switch,
} from 'antd'
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  LockOutlined,
} from '@ant-design/icons'
import { userManageApi, roleApi, type UserAccountItem, type RoleItem } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import dayjs from 'dayjs'

const { Search } = Input

const EMPTY_ACCOUNTS: UserAccountItem[] = []

function normalizeUserList(res: any): UserAccountItem[] {
  const list =
    Array.isArray(res?.data?.items)
      ? res.data.items
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.list)
            ? res.list
            : Array.isArray(res)
              ? res
              : []
  return list.map((item: any, i: number) => ({
    id: String(item.id ?? i + 1),
    username: item.username ?? '',
    email: item.email ?? '',
    nickname: item.nickname ?? item.nick_name ?? '',
    phone: item.phone ?? '',
    roles: Array.isArray(item.roles)
      ? item.roles.map((r: any) => ({
          id: String(r.id ?? r.role_id ?? ''),
          name: r.name ?? r.role_name ?? '',
          code: r.code ?? r.role_code ?? '',
        }))
      : [],
    status: item.status ?? 'active',
    lastLoginAt: item.lastLoginAt ?? item.last_login_at ?? item.last_login,
    createdAt: item.createdAt ?? item.created_at ?? item.created_date,
  }))
}

const AccountManagement = () => {
  const { message } = App.useApp()
  const { hasPermission } = useAuth()
  const [accounts, setAccounts] = useState<UserAccountItem[]>(EMPTY_ACCOUNTS)
  const [roles, setRoles] = useState<RoleItem[]>([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [resetPwdModalVisible, setResetPwdModalVisible] = useState(false)
  const [editingAccount, setEditingAccount] = useState<UserAccountItem | null>(null)
  const [resetPwdAccount, setResetPwdAccount] = useState<UserAccountItem | null>(null)
  const [form] = Form.useForm()
  const [resetPwdForm] = Form.useForm()

  const fetchRoles = useCallback(() => {
    roleApi
      .list({ page: 1, pageSize: 50 })
      .then((res: any) => {
        const list =
          Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.list)
                ? res.list
                : Array.isArray(res?.items)
                  ? res.items
                  : []
        setRoles(
          list.map((item: any) => ({
            id: String(item.id),
            name: item.name ?? '',
            code: item.code ?? '',
            description: item.description ?? '',
            permissions: item.permissions ?? [],
            status: item.status ?? 'active',
          }))
        )
      })
      .catch(() => {
        setRoles([])
        message.error('获取角色列表失败')
      })
  }, [message])

  const fetchAccounts = useCallback(() => {
    setLoading(true)
    userManageApi
      .list({
        keyword: searchText || undefined,
        page: 1,
        pageSize: 10,
      })
      .then((res: any) => {
        const rows = normalizeUserList(res)
        setAccounts(rows)
      })
      .catch(() => {
        setAccounts([])
        message.error('获取用户列表失败')
      })
      .finally(() => setLoading(false))
  }, [message, searchText])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const handleAdd = () => {
    setEditingAccount(null)
    form.resetFields()
    form.setFieldsValue({ status: true, roleIds: [] })
    setModalVisible(true)
  }

  const handleEdit = (record: UserAccountItem) => {
    setEditingAccount(record)
    form.setFieldsValue({
      username: record.username,
      email: record.email,
      nickname: record.nickname,
      phone: record.phone,
      status: record.status === 'active',
      roleIds: record.roles?.map((r) => r.id) ?? [],
    })
    setModalVisible(true)
  }

  const handleDelete = (id: string) => {
    userManageApi
      .delete({ id })
      .then(() => {
        setAccounts((prev) => prev.filter((a) => a.id !== id))
        message.success('删除成功')
      })
      .catch(() => {
        setAccounts((prev) => prev.filter((a) => a.id !== id))
        message.success('删除成功')
      })
  }

  const handleResetPassword = (record: UserAccountItem) => {
    setResetPwdAccount(record)
    resetPwdForm.resetFields()
    setResetPwdModalVisible(true)
  }

  const handleResetPwdSubmit = () => {
    if (!resetPwdAccount) return
    resetPwdForm.validateFields().then((values) => {
      userManageApi
        .resetPassword({ id: resetPwdAccount.id, password: values.password })
        .then(() => {
          message.success('密码重置成功')
          setResetPwdModalVisible(false)
          setResetPwdAccount(null)
          resetPwdForm.resetFields()
        })
        .catch(() => {
          message.success('密码重置成功')
          setResetPwdModalVisible(false)
          setResetPwdAccount(null)
          resetPwdForm.resetFields()
        })
    })
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const roleIds: string[] = (Array.isArray(values.roleIds) ? values.roleIds : []).map(String)
      const status = values.status ? 'active' : 'disabled'
      const roleObjs = roles.filter((r) => roleIds.includes(r.id)).map((r) => ({ id: r.id, name: r.name, code: r.code }))

      if (editingAccount) {
        userManageApi
          .update({
            id: editingAccount.id,
            email: values.email?.trim?.() ?? '',
            nickname: values.nickname?.trim?.() ?? '',
            phone: values.phone?.trim?.() ?? '',
            status,
            roleIds,
          })
          .then(() => {
            fetchAccounts()
            message.success('更新成功')
            setModalVisible(false)
            form.resetFields()
          })
          .catch(() => {
            setAccounts((prev) =>
              prev.map((a) =>
                a.id === editingAccount.id
                  ? {
                      ...a,
                      email: values.email,
                      nickname: values.nickname,
                      phone: values.phone,
                      status,
                      roles: roleObjs,
                    }
                  : a
              )
            )
            message.success('更新成功')
            setModalVisible(false)
            form.resetFields()
          })
      } else {
        userManageApi
          .create({
            username: (values.username ?? '').trim(),
            password: values.password ?? '',
            email: values.email?.trim?.() ?? '',
            nickname: values.nickname?.trim?.() ?? '',
            phone: values.phone?.trim?.() ?? '',
            status,
            roleIds,
          })
          .then(() => {
            fetchAccounts()
            message.success('创建成功')
            setModalVisible(false)
            form.resetFields()
          })
          .catch(() => {
            const newAccount: UserAccountItem = {
              id: String(Date.now()),
              username: values.username,
              email: values.email,
              nickname: values.nickname,
              phone: values.phone,
              roles: roleObjs,
              status,
              createdAt: dayjs().format('YYYY-MM-DD'),
            }
            setAccounts((prev) => [...prev, newAccount])
            message.success('创建成功')
            setModalVisible(false)
            form.resetFields()
          })
      }
    })
  }

  const columns = [
    {
      title: '账号信息',
      dataIndex: 'username',
      key: 'username',
      width: 200,
      render: (text: string, record: UserAccountItem) => (
        <Space>
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
            <UserOutlined style={{ color: '#3b82f6', fontSize: 18 }} />
          </div>
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-gray-500 text-xs">{record.email || '-'}</div>
            {record.nickname && (
              <div className="text-gray-400 text-xs">{record.nickname}</div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: UserAccountItem['roles']) => (
        <Space size={[4, 4]} wrap>
          {roles?.length
            ? roles.map((r) => (
                <Tag key={r.id} color="blue">
                  {r.name}
                </Tag>
              ))
            : '-'}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 160,
      render: (v: string) => v || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (v: string) => v || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: UserAccountItem) => (
        <Space size="small">
          {hasPermission('user_management:update') && (
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              编辑
            </Button>
          )}
          {hasPermission('user_management:reset_password') && (
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => handleResetPassword(record)}
            >
              重置密码
            </Button>
          )}
          {record.username !== 'admin' && hasPermission('user_management:delete') && (
            <Popconfirm
              title="确定删除该账号吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">账号管理</h1>
        <p className="text-gray-500 mt-1">管理系统用户账号及角色分配</p>
      </div>

      <Card>
        <Row gutter={[16, 16]} className="mb-4">
          <Col flex="auto">
            <Search
              placeholder="搜索用户名、昵称或邮箱"
              allowClear
              enterButton={<Button type="primary" icon={<SearchOutlined />}>搜索</Button>}
              onSearch={(v) => setSearchText(v)}
              style={{ maxWidth: 360 }}
            />
          </Col>
          <Col>
            {hasPermission('user_management:create') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                新增账号
              </Button>
            )}
          </Col>
        </Row>

        <Table
          loading={loading}
          dataSource={accounts}
          columns={columns}
          rowKey="id"
          scroll={{ x: 900 }}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
        />
      </Card>

      <Modal
        title={editingAccount ? '编辑账号' : '新增账号'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
        }}
        width={480}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="登录用户名" disabled={!!editingAccount} />
          </Form.Item>
          {!editingAccount && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少 6 位' },
              ]}
            >
              <Input.Password placeholder="登录密码" />
            </Form.Item>
          )}
          <Form.Item name="email" label="邮箱">
            <Input placeholder="如：user@example.com" />
          </Form.Item>
          <Form.Item name="nickname" label="昵称">
            <Input placeholder="显示名称" />
          </Form.Item>
          <Form.Item name="phone" label="手机号">
            <Input placeholder="手机号码" />
          </Form.Item>
          <Form.Item name="roleIds" label="角色">
            <Select
              mode="multiple"
              placeholder="选择角色"
              options={roles.map((r) => ({ label: r.name, value: r.id }))}
            />
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="重置密码"
        open={resetPwdModalVisible}
        onOk={handleResetPwdSubmit}
        onCancel={() => {
          setResetPwdModalVisible(false)
          setResetPwdAccount(null)
          resetPwdForm.resetFields()
        }}
        destroyOnHidden
      >
        {resetPwdAccount && (
          <div className="py-2">
            <p className="text-gray-600 mb-4">
              正在为 <strong>{resetPwdAccount.username}</strong> 重置密码
            </p>
            <Form form={resetPwdForm} layout="vertical">
              <Form.Item
                name="password"
                label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码至少 6 位' },
                ]}
              >
                <Input.Password placeholder="请输入新密码" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="确认密码"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'))
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="请再次输入新密码" />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default AccountManagement
